import { useState } from 'react';
import { IconButton, Modal } from 'src/Components';
import { SimpleSelect, EnumItemType } from 'src/Components/Controls';
import { useEQSelector } from 'src/Hooks';
import { IModalProps } from '..';
import { DenyReasonType } from 'src/Types/ReDoc/Enter';
import { Headline } from 'src/UI';

type ICompleteModalProps = IModalProps;

/** Модальное окно завершения работы с посетителем. Позволяет выбрать результат оказания услуги и причину неоказания (если услуга не оказана) */
const CompleteModal: React.FC<ICompleteModalProps> = ({
	type,
	isOpen,
	onAction,
	onClose,
}) => {
	const reasonList = useEQSelector<Array<DenyReasonType>>(
		type,
		'denyReasons',
	);
	const [result, setResult] = useState<string>(RESULT_SUCCESS);
	const [reason, setReason] = useState<string>('');
	const isDeniedResult = result === RESULT_DENY;

	/** Обработчик завершения работы. Отправляет "complete" если услуга оказана, "reasons" с причиной если не оказана */
	const onActionHandler = () => {
		if (isDeniedResult) {
			if (!reason) {
				return;
			}
			onAction('reasons', reason);
		} else {
			onAction('complete');
		}
		onClose();
	};

	/** Адаптер для setResult: принимает string | string[], но обрабатывает только string */
	const handleResultChange = (value: string | string[]) => {
		if (typeof value !== 'string') {
			return;
		}
		setResult(value);
		if (value !== RESULT_DENY) {
			setReason('');
		}
	};

	/** Рендерит содержимое модального окна. Отображает селекты для выбора результата и причины неоказания услуги */
	const renderBody: React.FC = () => {
		return (
			<div className="modal_wrapper modal_wrapper-redirect">
				<Headline>Результат оказания услуги</Headline>
				<SimpleSelect
					isOwnerList
					options={results}
					value={result}
					setValue={handleResultChange}
					placeholder="Выберите результат оказания услуги..."
				/>
				{isDeniedResult && (
					<>
						<Headline>Причина неоказания услуги</Headline>
						<SimpleSelect
							isOwnerList
							options={getEnums(reasonList)}
							value={reason}
							setValue={handleReasonChange}
							placeholder="Выберите причину неоказания услуги..."
						/>
					</>
				)}
			</div>
		);
	};

	/** Рендерит панель инструментов модального окна. Содержит кнопки "Отмена" и "Завершить прием" */
	const renderModalToolbar = () => {
		const isDisabled = isDeniedResult && !reason;
		return (
			<>
				<IconButton outline onClick={onClose}>
					Отмена
				</IconButton>
				<IconButton disable={isDisabled} onClick={onActionHandler}>
					Завершить прием
				</IconButton>
			</>
		);
	};

	/** Адаптер для setReason: принимает string | string[], но обрабатывает только string */
	const handleReasonChange = (value: string | string[]) => {
		if (typeof value === 'string') {
			setReason(value);
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			title="Завершение работы с посетителем"
			onClose={onClose}
			color="light"
			className="not_scrolled"
			renderFooter={renderModalToolbar()}
			renderBody={renderBody}
		/>
	);
};

/** Список возможных результатов оказания услуги */
const RESULT_SUCCESS = '0';
const RESULT_DENY = '1';

const results: Array<EnumItemType> = [
	{
		id: RESULT_SUCCESS,
		title: 'Услуга оказана',
	},
	{
		id: RESULT_DENY,
		title: 'Услуга не оказана',
	},
];

/** Преобразует список причин отказа в формат для селекта */
const getEnums = (items: Array<DenyReasonType>): Array<EnumItemType> => {
	return items.map((item: DenyReasonType) => {
		return {
			id: item.id,
			title: item.name,
		};
	});
};

export default CompleteModal;
